import { AxiosResponse } from 'axios';
import { Event } from './shared/eventstore-client/EventstoreClient';
import { ServiceClientWithEventstore } from './shared/service-client/ServiceClientWithEventstore';

export enum ThemeAppearance {
  ELEGANT = 'elegant',
  CRAFTMANSHIP = 'craftmanship',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}
export enum IdentificationMethod {
  NFC = 'nfc',
  QR = 'qr',
}

export enum SoMe {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  PINTEREST = 'pinterest',
  REDDIT = 'reddit',
  SNAPCHAT = 'snapchat',
  SPOTIFY = 'spotify',
  TELEGRAM = 'telegram',
  TUMBLR = 'tumblr',
  TWITTER = 'twitter',
  VIBER = 'viber',
  VIMEO = 'vimeo',
  VINE = 'vine',
  WEBSITE = 'website',
  WHATSAPP = 'whatsapp',
  YOUTUBE = 'youtube',
}

export interface SocialMedia {
  name: SoMe;
  url: string;
  order?: number;
}
export interface OrganizationSettings {
  displayName?: string;
  productIdentification?: {
    methods?: IdentificationMethod[];
  };
  theme?: {
    primaryColor?: string;
    backgroundColor?: string;
    headingColor?: string;
    paragraphColor?: string;
    iconColor?: string;
    buttonTextColor?: string;
    provenanceSortDirection?: SortDirection;
    appearance?: ThemeAppearance;
  };
  links?: {
    termsOfService?: string;
    privacyPolicy?: string;
    cookiePolicy?: string;
  };
  logoUrl?: string;
  emailLogoUrl?: string;
  fromEmail?: string;
  socialMedia?: SocialMedia[];
  summaryEmailReceivers?: { ownershipRequests?: string[] };
}

export interface Organization {
  name: string;
  settings?: OrganizationSettings;
}

export interface PublicOrganizationSettings
  extends Pick<
    OrganizationSettings,
    'displayName' | 'links' | 'logoUrl' | 'theme' | 'productIdentification' | 'socialMedia'
  > {
  id: string;
}

export type UpdateOrganizationSettings = OrganizationSettings;

export type PERMISSIONS = typeof GET | typeof UPDATE_SETTINGS;

export const GET = 'ORGANIZATION:GET';
export const UPDATE_SETTINGS = 'ORGANIZATION:UPDATE:SETTINGS';
export const GET_EVENTS_ANY = 'ORGANIZATION:GET_EVENTS:ANY';
export const GET_EVENTS_SETTINGS_UPDATED = 'ORGANIZATION:GET_EVENTS:SETTINGS_UPDATED';

export const PERMISSION_NAMES = {
  GET,
  UPDATE_SETTINGS,
  GET_EVENTS_ANY,
  GET_EVENTS_SETTINGS_UPDATED,
};

export interface UpdatedSettingsEvent extends Event {
  type: 'UPDATED_SETTINGS';
  data: UpdateOrganizationSettings;
}

export type OrganizationEvents = UpdatedSettingsEvent;

export class OrganizationClient extends ServiceClientWithEventstore<OrganizationEvents> {
  public async getMyOrganization(): Promise<AxiosResponse<Organization>> {
    return await this.httpClient.get(`/my`, this.requestConfig);
  }

  public async getPublicSettings({ orgName }: { orgName: string }): Promise<AxiosResponse<PublicOrganizationSettings>> {
    return await this.httpClient.get(`/public-settings`, {
      params: {
        organizationName: orgName,
      },
      ...this.requestConfig,
    });
  }

  public async updateSettings(updatedSettings: UpdateOrganizationSettings): Promise<AxiosResponse<Organization>> {
    return await this.httpClient.put(`/my/settings`, updatedSettings, this.requestConfig);
  }
}
